import { isWithinTrackGap } from "../../constants";
import type { BoxCoordinates } from "../../utils/coordinates";
import { toPresentationUs } from "../../utils/time";
import type { FramePrediction, IntervalResult, TrackingInterval } from "../contracts";

/** Decoder timestamps are integer microseconds, even when represented as seconds. */
export const frameKey = toPresentationUs;

export function recoveryIntervalKey(interval: TrackingInterval): string {
  return JSON.stringify([interval.trackId, interval.start, interval.end]);
}

/** Only original, eligible GT pairs with final rejected interior frames can request recovery. */
export function recoveryCandidate(result: IntervalResult): FramePrediction | undefined {
  const { interval, frames } = result;
  const gap = interval.end.time - interval.start.time;
  if (result.phase === "sparse" || !(gap > 0) || !isWithinTrackGap(interval.start.time, interval.end.time))
    return undefined;
  let longest: FramePrediction[] = [];
  let run: FramePrediction[] = [];
  const length = (items: FramePrediction[]) =>
    items.length ? items.at(-1)!.timestamp + items.at(-1)!.duration - items[0]!.timestamp : 0;
  const finish = () => {
    if (length(run) > length(longest)) longest = run;
    run = [];
  };
  for (let index = 1; index < frames.length - 1; index++) {
    const frame = frames[index]!;
    if (
      frame.method === "unavailable" &&
      frame.timestamp > interval.start.time &&
      frame.timestamp < interval.end.time
    ) {
      run.push(frame);
    } else finish();
  }
  finish();
  if (!longest.length) return undefined;
  const midpoint =
    (frameKey(longest[0]!.timestamp) + frameKey(longest.at(-1)!.timestamp + longest.at(-1)!.duration)) / 2;
  return longest.reduce((best, frame) =>
    Math.abs(frameKey(frame.timestamp) - midpoint) < Math.abs(frameKey(best.timestamp) - midpoint) ? frame : best,
  );
}

export function validRecoveryBox(box: BoxCoordinates): boolean {
  const { top_left: topLeft, bottom_right: bottomRight } = box;
  return (
    [topLeft.x, topLeft.y, bottomRight.x, bottomRight.y].every(
      (coordinate) => Number.isFinite(coordinate) && coordinate >= 0 && coordinate <= 1000,
    ) &&
    topLeft.x < bottomRight.x &&
    topLeft.y < bottomRight.y
  );
}
